import React, { useState, useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import HomeIcon from "@mui/icons-material/Home";
import EditIcon from "@mui/icons-material/Edit";
import HistoryIcon from "@mui/icons-material/History";
import CreditCardIcon from "@mui/icons-material/CreditCard";
import LogoutIcon from "@mui/icons-material/Logout";
import SettingsIcon from "@mui/icons-material/Settings";
import MenuIcon from "@mui/icons-material/Menu";
import Logo from "../logo.png";
import { logOut } from "../api/auth";
import { getUserCredits } from "../api/businesses";
import { getStripeUrlForPurchasingPhotoCredits } from "../api/stripe";
import { useCredits } from "../context/CreditsContext";
declare global {
  interface Window {
    lintrk?: (action: string, data: { conversion_id: number }) => void;
  }
}

const Sidebar: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [showCreditModal, setShowCreditModal] = useState(false);
  const [creditsToAdd, setCreditsToAdd] = useState("10");
  const { credits, setCredits } = useCredits();
  const [isCollapsed, setIsCollapsed] = useState(window.innerWidth <= 768); // Automatically collapse on small screens

  // Handle screen resizing to auto-collapse on small screens
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth <= 768) {
        setIsCollapsed(true);
      }
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Fetch credits
  useEffect(() => {
    const fetchCredits = async () => {
      try {
        const res = await getUserCredits();
        setCredits(res.data.credits);
      } catch (error) {
        console.error("Error fetching credits:", error);
      }
    };

    fetchCredits();
  }, [setCredits]);

  // Handle Logout
  const handleLogout = () => {
    logOut();
    navigate("/login");
  };

  // Handle Purchase Credit Button Click
  const handlePurchaseCredits = async () => {
    try {
      const creditsAmount = parseInt(creditsToAdd, 10) || 1;
      const stripeUrl = await getStripeUrlForPurchasingPhotoCredits(
        creditsAmount
      );
      const checkoutUrl = stripeUrl.data.url;
      window.location.href = checkoutUrl;

      // LinkedIn Tracking after purchase (if needed on successful completion)
      // Triggered only after successful purchase; ensure this is done once user returns from Stripe
      // In your case, you could call this after confirming purchase completion on your backend
      if (
        typeof window !== "undefined" &&
        typeof window.lintrk === "function"
      ) {
        window.lintrk("track", { conversion_id: 19540428 });
      }

      setShowCreditModal(false);
    } catch (error) {
      console.error("Error initiating purchase:", error);
    }
  };

  const handleCreditsToAddChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    if (value === "" || /^[1-9]\d*$/.test(value)) {
      setCreditsToAdd(value);
    }
  };

  // Navigation Items
  const navItems = [
    { name: "Furnish Home", path: "/furnish-home", icon: <HomeIcon /> },
    { name: "Remove Furniture", path: "/remove-furniture", icon: <EditIcon /> },
    { name: "History", path: "/history", icon: <HistoryIcon /> },
    { name: "Settings", path: "/settings", icon: <SettingsIcon /> },
  ];

  return (
    <div
      className={`${
        isCollapsed ? "w-16" : "w-64"
      } h-full bg-gray-100 text-gray-800 flex flex-col border-r relative transition-all duration-300`}
    >
      {/* Toggle Button */}
      <button
        onClick={() => setIsCollapsed(!isCollapsed)}
        className="absolute top-4 left-4 bg-gray-200 p-2 rounded-full hover:bg-gray-300"
      >
        <MenuIcon />
      </button>

      {/* Logo */}
      <div
        className={`flex items-center ${
          isCollapsed ? "justify-center" : "justify-start"
        } p-6`}
      >
        <img
          src={Logo}
          alt="Virtual Stage Pro Logo"
          className={`transition-all duration-300 ${
            isCollapsed ? "h-10 w-10" : "h-15 w-auto"
          }`}
        />
      </div>

      {/* Navigation Links */}
      <nav className="flex-grow">
        {navItems.map((item) => (
          <Link
            key={item.name}
            to={item.path}
            className={`flex items-center ${
              isCollapsed ? "justify-center" : "justify-start"
            } px-6 py-3 text-lg hover:bg-gray-200 transition-all duration-300 ${
              location.pathname === item.path ? "bg-gray-200" : ""
            }`}
          >
            {item.icon}
            {!isCollapsed && <span className="ml-3">{item.name}</span>}
          </Link>
        ))}
      </nav>

      {/* Footer Section */}
      <div className="p-4 border-t border-gray-200">
        {!isCollapsed && (
          <span className="block text-center text-m text-gray-600 mb-2">
            Remaining Credits: {credits}
          </span>
        )}

        <button
          onClick={() => setShowCreditModal(true)}
          className={`${
            isCollapsed
              ? "bg-green-500 flex items-center justify-center p-2 rounded-full hover:bg-green-600 w-10 h-10 mx-auto"
              : "bg-green-500 text-white flex items-center justify-center py-2 rounded-lg hover:bg-green-600 w-full mb-4"
          }`}
        >
          <CreditCardIcon className={`${isCollapsed ? "" : "mr-2"}`} />
          {!isCollapsed && "Add Credits"}
        </button>

        <button
          onClick={handleLogout}
          className={`${
            isCollapsed
              ? "bg-red-500 flex items-center justify-center p-2 rounded-full hover:bg-red-600 w-10 h-10 mx-auto"
              : "flex items-center px-6 py-2 text-lg text-red-600 hover:bg-gray-200 w-full text-left"
          }`}
        >
          <LogoutIcon className={`${isCollapsed ? "" : "mr-3"}`} />
          {!isCollapsed && "Logout"}
        </button>
      </div>

      {/* Credit Purchase Modal */}
      {showCreditModal && (
        <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50">
          <div className="bg-white p-6 rounded-lg shadow-lg w-80">
            <h2 className="text-2xl font-bold mb-4">Buy Photo Credits</h2>
            <label className="block text-sm font-medium text-gray-700">
              Number of Photos
            </label>
            <input
              type="number"
              min="1"
              value={creditsToAdd}
              onChange={handleCreditsToAddChange}
              className="w-full p-2 mt-2 border border-gray-300 rounded-lg focus:outline-none focus:border-blue-500"
              placeholder="10"
            />
            <button
              onClick={handlePurchaseCredits}
              className="w-full mt-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition duration-200"
            >
              Buy Now
            </button>
            <button
              onClick={() => setShowCreditModal(false)}
              className="w-full mt-2 py-2 bg-gray-300 text-gray-700 rounded-lg hover:bg-gray-400 transition duration-200"
            >
              Cancel
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default Sidebar;
